import {
  IconDirections,
  IconLanguage,
  IconMusic,
  IconSparkles,
} from "@tabler/icons-react";
import AiPromptEditModal from "@components/mode_tool/AiPromptEditModal";
import AiToolsPanel from "@components/ai_tools/AiToolsPanel";
import { useState } from "react";

function RouteActionsOverlayPanel({
  anchorRef,
  selectedMode,
  activeLanguageIso,
  onStopWalkingRouteShapeEdit,
  onTranslatePoints,
  onGenerateTurnByTurn,
  isGeneratingTurnByTurn = false,
  turnByTurnApplied = false,
  isAiAutomationRunning = false,
  onAutomatePoints,
  onGenerateDirectionPoints,
  onGenerateParkingPoint,
  onGenerateImages,
  onGenerateAudioDescription,
  onGenerateMusic,
  isGeneratingMusic = false,
  isBatchAudioBusy = false,
}) {
  const [editingPromptAction, setEditingPromptAction] = useState(null);
  const mode = String(selectedMode || "").toLowerCase();
  const isEnglish = String(activeLanguageIso || "").toLowerCase() === "en";
  const supportsRouteAi = ["walking", "driving"].includes(mode) && isEnglish;

  if (!selectedMode || !isEnglish) return null;

  const runAction = (callback) => {
    onStopWalkingRouteShapeEdit?.();
    callback?.();
  };

  const actions = [
    {
      key: "turn-by-turn",
      label: isGeneratingTurnByTurn
        ? "Generating MB Turn by Turn…"
        : "MB Turn by Turn",
      icon: isGeneratingTurnByTurn ? (
        <span className="mode-route-tool-btn-spinner" />
      ) : (
        <IconDirections size={17} />
      ),
      onClick: () => runAction(onGenerateTurnByTurn),
      disabled:
        isGeneratingTurnByTurn || turnByTurnApplied || !onGenerateTurnByTurn,
      visible: mode === "driving" && isEnglish,
    },
    {
      key: "automate-points",
      label: "AI Automate Points",
      editTitle: "AI Automate Points",
      promptKey: "automate_points",
      icon: <IconSparkles size={17} />,
      onClick: () => runAction(onAutomatePoints),
      disabled: !onAutomatePoints || isAiAutomationRunning,
      visible: supportsRouteAi,
    },
    {
      key: "direction-points",
      label: "AI Direction Points",
      editTitle: "AI Direction Points",
      promptKey: "directions",
      icon: <IconSparkles size={17} />,
      onClick: () => runAction(onGenerateDirectionPoints),
      disabled: !onGenerateDirectionPoints || isAiAutomationRunning,
      visible: mode === "driving" && isEnglish,
    },
    {
      key: "parking-point",
      label: "AI Parking Points",
      editTitle: "AI Parking Points",
      promptKey: "parking",
      icon: <IconSparkles size={17} />,
      onClick: () => runAction(onGenerateParkingPoint),
      disabled: !onGenerateParkingPoint || isAiAutomationRunning,
      visible: mode === "driving" && isEnglish,
    },
    {
      key: "generate-images",
      label: "AI Generate Images",
      editTitle: "AI Generate Images",
      promptKey: "images",
      icon: <IconSparkles size={17} />,
      onClick: () => runAction(onGenerateImages),
      disabled: !onGenerateImages || isAiAutomationRunning,
      visible: supportsRouteAi,
    },
  ].filter((action) => action.visible);

  actions.push(
    {
      key: "translate",
      label: "AI Translate Points",
      editTitle: "AI Translate Points",
      promptKey: "translate",
      icon: <IconLanguage size={17} />,
      onClick: () => runAction(onTranslatePoints),
      disabled: !onTranslatePoints,
      visible: true,
    },
    {
      key: "ai-audio-description",
      label: "AI Audio Description",
      editTitle: "AI Audio Description",
      icon: <IconSparkles size={17} />,
      onClick: () => runAction(onGenerateAudioDescription),
      disabled: !onGenerateAudioDescription || isAiAutomationRunning,
      visible: supportsRouteAi,
    },
    {
      key: "ai-music",
      label: isGeneratingMusic ? "AI Music Generating…" : "AI Music Background",
      editTitle: "AI Music Background",
      promptKey: "music",
      icon: isGeneratingMusic ? (
        <span className="mode-route-tool-btn-spinner" />
      ) : (
        <IconMusic size={17} />
      ),
      onClick: () => runAction(onGenerateMusic),
      disabled: !onGenerateMusic || isGeneratingMusic,
      visible: ["driving", "walking", "random"].includes(mode) && isEnglish,
    },
  );

  if (isBatchAudioBusy) {
    actions.forEach((action) => {
      action.disabled = true;
    });
  }

  if (!actions.length) return null;

  return (
    <>
      <AiToolsPanel
        anchorRef={anchorRef}
        title="AI TOOLS"
        ariaLabel="AI TOOLS"
        isToggleDisabled={isBatchAudioBusy}
        actions={actions.map((action) => ({
          ...action,
          id: action.key,
          onEdit: ["turn-by-turn", "ai-audio-description"].includes(action.key)
            ? null
            : () => setEditingPromptAction(action),
          editDisabled: isBatchAudioBusy,
        }))}
      />
      <AiPromptEditModal
        isOpen={Boolean(editingPromptAction)}
        action={editingPromptAction}
        onClose={() => setEditingPromptAction(null)}
      />
    </>
  );
}

export default RouteActionsOverlayPanel;
